import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchJobsList } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';

// Stage 4 (read-only slice, docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8):
// invoices/payments status. Reuses the existing jobsList bridge action —
// every job object already carries total/amountPaid/balance (the same
// fields the current HTML UI's billing screen reads), so this needed no
// new backend action at all. Recording a payment, generating an invoice,
// or building/signing a contract is deliberately not included here — like
// the Estimate builder, that is protected-financial write logic that
// deserves its own dedicated, carefully-tested pass rather than being
// rushed alongside this read-only view.
export function BillingPage() {
  const query = useQuery({ queryKey: ['billing'], queryFn: () => fetchJobsList('', '') });

  const { outstanding, paid } = useMemo(() => {
    const jobs = (query.data?.jobs ?? []).filter((j) => j.stage === 'Invoice' || j.stage === 'Paid');
    return {
      outstanding: jobs.filter((j) => j.stage === 'Invoice').sort((a, b) => Number(b.balance ?? 0) - Number(a.balance ?? 0)),
      paid: jobs.filter((j) => j.stage === 'Paid'),
    };
  }, [query.data]);

  if (query.isLoading) return <LoadingState label="Loading billing…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load billing.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load billing.'} onRetry={() => query.refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance" title="Billing" />

      <Card>
        <CardHeader title="Outstanding Invoices" />
        {outstanding.length === 0 ? (
          <EmptyState>No outstanding invoices.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {outstanding.map((job) => (
              <li key={job.jobId}>
                <Link
                  to={`/jobs/${encodeURIComponent(job.jobId)}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3.5 transition hover:border-gold-700/60 hover:bg-ink-raised"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{job.customer || job.jobId}</p>
                    <p className="truncate text-sm text-slate-400">{job.project || job.documentNo}</p>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-gold-200">{String(job.balanceText ?? '—')}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Recently Paid" />
        {paid.length === 0 ? (
          <EmptyState>No paid invoices yet.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {paid.slice(0, 20).map((job) => (
              <li key={job.jobId}>
                <Link
                  to={`/jobs/${encodeURIComponent(job.jobId)}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3.5 transition hover:border-gold-700/60 hover:bg-ink-raised"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{job.customer || job.jobId}</p>
                    <p className="truncate text-sm text-slate-400">{job.project || job.documentNo}</p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-slate-300">{String(job.totalText ?? job.amountPaidText ?? '—')}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
